import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { useMutation, useQuery } from 'react-query';
import api from '../../services/api';

/**
 * 임대관리사업자용 빌링 관리 컴포넌트
 * 소속 모든 임대사업자의 빌링 작업 관리
 */
const RentalManagementCompanyBilling = () => {
  // 모바일 감지 함수
  const isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  };

  const [activeTab, setActiveTab] = useState('billing-work');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedBuilding, setSelectedBuilding] = useState('');
  const [selectedOwnerIds, setSelectedOwnerIds] = useState([]); // 선택된 임대사업자 ID 목록
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    billing_month: '',
    work_week_start: '',
    billing_type: 'weekly'
  });

  // 빌링 내역 관련 상태
  const [historyPage, setHistoryPage] = useState(1);
  const [historyFilters, setHistoryFilters] = useState({
    billing_month: '',
    property_owner_id: '',
    building_id: '',
    due_date_start: '',
    due_date_end: ''
  });

  // 빌링 보고서 관련 상태
  const [reportType, setReportType] = useState('monthly');
  const [reportFilters, setReportFilters] = useState({
    start_month: '',
    end_month: '',
    property_owner_id: ''
  });

  // 빌링상세 관련 상태
  const [selectedStatement, setSelectedStatement] = useState(null);
  const [showStatementDetail, setShowStatementDetail] = useState(false);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [deliveryForm, setDeliveryForm] = useState({
    method: 'email', // email, sms
    template_id: '',
    custom_message: ''
  });
  const [selectedStatements, setSelectedStatements] = useState([]);
  
  // 일괄발송 관련 상태
  const [showBatchDeliveryModal, setShowBatchDeliveryModal] = useState(false);
  const [batchDeliveryForm, setBatchDeliveryForm] = useState({
    method: 'email', // email, sms
    template_id: '',
    custom_message: ''
  });

  // 소속 임대사업자 목록 조회
  const { data: propertyOwnersData } = useQuery(
    'property-owners',
    () => api.get('/billing-work/property-owners'),
    {
      onError: (error) => {
        console.error('임대사업자 목록 조회 오류:', error);
        toast.error('임대사업자 목록을 불러오는데 실패했습니다.');
      }
    }
  );

  // 건물 목록 조회
  const { data: buildingsData } = useQuery(
    ['buildings', selectedOwnerIds],
    () => api.get('/billing-work/buildings', {
      params: {
        property_owner_id: selectedOwnerIds.length === 1 ? selectedOwnerIds[0] : undefined
      }
    }),
    {
      enabled: selectedOwnerIds.length > 0,
      onError: (error) => {
        console.error('건물 목록 조회 오류:', error);
      }
    }
  );

  // 메시지 템플릿 조회
  const { data: messageTemplatesData } = useQuery(
    'message-templates',
    () => api.get('/vacancy/message-templates'),
    {
      onError: (error) => {
        console.error('메시지 템플릿 조회 오류:', error);
        toast.error('메시지 템플릿을 불러오는데 실패했습니다.');
      }
    }
  );

  // 빌링 세션 조회
  const { data: sessionsData, refetch: refetchSessions } = useQuery(
    ['billing-sessions', selectedMonth, selectedBuilding],
    () => api.get('/billing-work/sessions', {
      params: {
        billing_month: selectedMonth || undefined,
        building_id: selectedBuilding || undefined
      }
    }),
    {
      onError: (error) => {
        console.error('빌링 세션 조회 오류:', error);
        toast.error('빌링 세션을 불러오는데 실패했습니다.');
      }
    }
  );

  // 빌링 내역 조회
  const { data: historyData, refetch: refetchHistory } = useQuery(
    ['billing-history', historyPage, historyFilters],
    () => api.get('/billing-work/history', {
      params: {
        page: historyPage,
        per_page: 20,
        billing_month: historyFilters.billing_month || undefined,
        property_owner_id: historyFilters.property_owner_id || undefined,
        building_id: historyFilters.building_id || undefined,
        due_date_start: historyFilters.due_date_start || undefined,
        due_date_end: historyFilters.due_date_end || undefined
      }
    }),
    {
      enabled: activeTab === 'billing-history',
      onError: (error) => {
        console.error('빌링 내역 조회 오류:', error);
        toast.error('빌링 내역을 불러오는데 실패했습니다.');
      }
    }
  );

  // 빌링 보고서 조회
  const { data: reportsData, refetch: refetchReports } = useQuery(
    ['billing-reports', reportType, reportFilters],
    () => api.get('/billing-work/reports', {
      params: {
        report_type: reportType,
        start_month: reportFilters.start_month || undefined,
        end_month: reportFilters.end_month || undefined,
        property_owner_id: reportFilters.property_owner_id || undefined
      }
    }),
    {
      enabled: activeTab === 'billing-reports',
      onError: (error) => {
        console.error('빌링 보고서 조회 오류:', error);
        toast.error('빌링 보고서를 불러오는데 실패했습니다.');
      }
    }
  );

  // 빌링상세 조회
  const { data: statementDetailData, refetch: refetchStatementDetail } = useQuery(
    ['billing-statement-detail', selectedStatement?.statement_id],
    () => api.get(`/billing-work/statement/${selectedStatement.statement_id}`),
    {
      enabled: !!selectedStatement?.statement_id,
      onError: (error) => {
        console.error('빌링상세 조회 오류:', error);
        toast.error('빌링상세를 불러오는데 실패했습니다.');
      }
    }
  );

  // 템플릿 변수 치환 함수
  const replaceTemplateVariables = (content, statementData) => {
    if (!content || !statementData) return content;
    
    const replacements = {
      '{tenant_name}': statementData.tenant?.name || '',
      '{building_name}': statementData.building?.name || '',
      '{unit_number}': statementData.unit?.unit_number || '',
      '{property_owner_name}': statementData.property_owner?.name || '',
      '{company_name}': statementData.property_owner?.company_name || statementData.property_owner?.name || '',
      '{contact_phone}': statementData.property_owner?.phone || '',
      '{contact_email}': statementData.property_owner?.email || '',
      '{billing_month}': statementData.billing_month || '',
      '{total_amount}': statementData.total_amount?.toLocaleString() || '',
      '{due_date}': statementData.due_date ? `${statementData.due_date}일` : '',
      '{statement_id}': statementData.statement_id || '',
    };

    let result = content;
    for (const [placeholder, value] of Object.entries(replacements)) {
      result = result.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), value);
    }
    return result;
  };

  // 청구서 전달 모달 열기
  const handleOpenDeliveryModal = (statementId) => {
    setSelectedStatement({ statement_id: statementId });
    setShowDeliveryModal(true);
    setDeliveryForm({
      method: isMobile() ? 'sms' : 'email', // 모바일에서는 SMS, PC에서는 이메일 기본값
      template_id: '',
      custom_message: ''
    });
    // 빌링 상세 정보 강제 새로고침
    refetchStatementDetail();
  };

  const handleSendRentDueKakao = async (statementId) => {
    if (!window.confirm('청구 납부안내 알림톡을 발송 요청할까요?')) return;
    try {
      const response = await api.post(`/message-master/rent-due/${statementId}/send`, {});
      toast.success(`알림톡 요청 완료: ${response.status || response.data?.status || 'READY'}`);
    } catch (error) {
      toast.error(error.response?.data?.message || '알림톡 요청에 실패했습니다.');
    }
  };

  // 이메일 발송 함수 (PC에서만)
  const handleEmailDelivery = async (statementId, method, templateId, customMessage) => {
    if (isMobile()) {
      toast.error('PC에서 이메일을 발송해보세요!');
      return;
    }

    try {
      const response = await api.post(`/billing-work/statement/${statementId}/deliver`, {
        method: method,
        template_id: templateId,
        custom_message: customMessage
      });

      if (response.data.success) {
        toast.success('이메일 발송이 완료되었습니다.');
        return true;
      } else {
        toast.error(`이메일 발송에 실패했습니다: ${response.data.error}`);
        return false;
      }
    } catch (error) {
      console.error('이메일 발송 오류:', error);
      toast.error('이메일 발송에 실패했습니다.');
      return false;
    }
  };

  // SMS 발송 함수 (모바일에서만)
  const handleSMSDelivery = (phone, message) => {
    if (!isMobile()) {
      toast.error('모바일에서 SMS를 발송해보세요!');
      return;
    }

    // 모바일에서 SMS 앱 호출
    const smsLink = `sms:${phone}?body=${encodeURIComponent(message)}`;
    window.open(smsLink);
    toast.success('SMS 앱이 열렸습니다. 메시지를 확인하고 발송해주세요.');
  };

  // 빌링 세션 취소 함수
  const handleCancelSession = async (sessionId) => {
    if (!window.confirm('정말로 이 빌링 세션을 취소하시겠습니까? 취소된 청구서는 복구할 수 없습니다.')) {
      return;
    }

    try {
      const response = await api.post(`/billing-work/sessions/${sessionId}/cancel`);
      
      if (response.data.success) {
        toast.success(response.data.message);
        // 세션 목록 새로고침
        refetchSessions();
      } else {
        toast.error(response.data.error || '빌링 세션 취소에 실패했습니다.');
      }
    } catch (error) {
      console.error('빌링 세션 취소 오류:', error);
      toast.error('빌링 세션 취소에 실패했습니다.');
    }
  };

  // 빌링 세션 재계산 함수
  const handleRecalculateSession = async (sessionId) => {
    if (!window.confirm('정말로 이 빌링 세션을 재계산하시겠습니까? 기존 청구서가 삭제되고 새로 생성됩니다.')) {
      return;
    }

    try {
      const response = await api.post(`/billing-work/sessions/${sessionId}/recalculate`);
      
      if (response.data.success) {
        toast.success(response.data.message);
        // 세션 목록 새로고침
        refetchSessions();
      } else {
        toast.error(response.data.error || '빌링 세션 재계산에 실패했습니다.');
      }
    } catch (error) {
      console.error('빌링 세션 재계산 오류:', error);
      toast.error('빌링 세션 재계산에 실패했습니다.');
    }
  };

  // 청구서 전달 함수
  const handleDeliverStatement = async () => {
    if (!selectedStatement?.statement_id) return;
    
    if (deliveryForm.method === 'email') {
      // 이메일 발송 (PC에서만)
      const success = await handleEmailDelivery(
        selectedStatement.statement_id,
        deliveryForm.method,
        deliveryForm.template_id,
        deliveryForm.custom_message
      );
      
      if (success) {
        setShowDeliveryModal(false);
        // 빌링 내역 새로고침
        refetchHistory();
        // 상세 정보 새로고침
        if (showStatementDetail) {
          refetchStatementDetail();
        }
      }
    } else if (deliveryForm.method === 'sms') {
      // SMS 발송 (모바일에서만)
      if (statementDetailData?.data?.tenant?.phone) {
        const message = deliveryForm.custom_message || '청구서가 발송되었습니다.';
        handleSMSDelivery(statementDetailData.data.tenant.phone, message);
        setShowDeliveryModal(false);
      } else {
        toast.error('임차인 전화번호가 없습니다.');
      }
    }
  };

  // 일괄발송 모달 열기
  const handleOpenBatchDeliveryModal = () => {
    if (selectedStatements.length === 0) {
      toast.error('전달할 청구서를 선택해주세요.');
      return;
    }
    setShowBatchDeliveryModal(true);
    setBatchDeliveryForm({
      method: isMobile() ? 'sms' : 'email', // 모바일에서는 SMS, PC에서는 이메일 기본값
      template_id: '',
      custom_message: ''
    });
  };

  // 일괄발송 실행
  const handleBatchDeliver = async () => {
    if (batchDeliveryForm.method === 'email') {
      // 이메일 일괄발송 (PC에서만)
      if (isMobile()) {
        toast.error('PC에서 이메일을 발송해보세요!');
        return;
      }

      try {
        const response = await api.post('/billing-work/statements/batch-deliver', {
          statement_ids: selectedStatements,
          method: batchDeliveryForm.method,
          template_id: batchDeliveryForm.template_id,
          custom_message: batchDeliveryForm.custom_message
        });
        
        // 응답 구조 확인: response.data.success 또는 전체 결과 확인
        const isSuccess = response.data.success === true;
        const successCount = response.data.data?.success_count || 0;
        const errorCount = response.data.data?.error_count || 0;
        
        if (isSuccess) {
          if (errorCount > 0) {
            // 일부 성공, 일부 실패
            toast.success(response.data.message, { duration: 4000 });
          } else {
            // 모두 성공
            toast.success(response.data.message);
          }
          setShowBatchDeliveryModal(false);
          setSelectedStatements([]);
          // 빌링 내역 새로고침
          refetchHistory();
        } else {
          // 전체 실패
          const errorMsg = response.data.message || response.data.error || '일괄 전달에 실패했습니다.';
          toast.error(errorMsg, { duration: 5000 });
          // 실패한 항목 정보 표시 (선택사항)
          if (response.data.data?.delivery_results) {
            const failedItems = response.data.data.delivery_results.filter(r => !r.success);
            if (failedItems.length > 0) {
              console.error('실패한 항목:', failedItems);
            }
          }
        }
      } catch (error) {
        console.error('일괄 전달 오류:', error);
        console.error('오류 응답:', error.response?.data);
        const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || '일괄 전달에 실패했습니다.';
        toast.error(errorMessage);
      }
    } else if (batchDeliveryForm.method === 'sms') {
      // SMS 일괄발송 (모바일에서만)
      if (!isMobile()) {
        toast.error('모바일에서 SMS를 발송해보세요!');
        return;
      }

      // 선택된 청구서들의 전화번호와 메시지를 순차적으로 SMS 앱으로 전송
      const statements = historyData?.data?.history?.filter(item => 
        selectedStatements.includes(item.statement_id)
      ) || [];

      if (statements.length === 0) {
        toast.error('전달할 청구서 정보를 찾을 수 없습니다.');
        return;
      }

      // 첫 번째 SMS 앱 호출
      const firstStatement = statements[0];
      if (firstStatement.tenant_phone) {
        const message = batchDeliveryForm.custom_message || '청구서가 발송되었습니다.';
        handleSMSDelivery(firstStatement.tenant_phone, message);
        
        // 나머지 청구서들을 1초 간격으로 순차 발송
        for (let i = 1; i < statements.length; i++) {
          setTimeout(() => {
            const statement = statements[i];
            if (statement.tenant_phone) {
              handleSMSDelivery(statement.tenant_phone, message);
            }
          }, i * 1000);
        }
        
        toast.success(`${statements.length}건의 SMS 앱이 순차적으로 열립니다.`);
        setShowBatchDeliveryModal(false);
        setSelectedStatements([]);
      } else {
        toast.error('임차인 전화번호가 없는 청구서가 있습니다.');
      }
    }
  };

  // 납기일 기준 자동 선택
  const handleSelectByDueDate = async (daysBefore = 3, daysAfter = 5, specificOwnerId = null) => {
    try {
      const params = {
        days_before: daysBefore,
        days_after: daysAfter,
        status: 'confirmed'
      };

      // 특정 임대사업자 ID가 제공되면 우선 사용, 그렇지 않으면 현재 필터 사용
      if (specificOwnerId) {
        params.property_owner_id = specificOwnerId;
        console.log(`🎯 특정 임대사업자 ${specificOwnerId} 대상으로 납기일 기준 선택`);
      } else if (historyFilters.property_owner_id) {
        params.property_owner_id = historyFilters.property_owner_id;
        console.log(`🎯 현재 필터 임대사업자 ${historyFilters.property_owner_id} 대상으로 납기일 기준 선택`);
      } else {
        console.log(`🌐 전체 임대사업자 대상으로 납기일 기준 선택`);
      }

      const response = await api.get('/billing-work/statements/due-date-filter', {
        params: params
      });

      console.log('🔍 납기일 기준 선택 API 응답:', response);
      console.log('🔍 응답 성공 여부:', response.success);
      console.log('🔍 응답 데이터:', response.data);
      console.log('🔍 전체 응답 구조:', JSON.stringify(response, null, 2));

      // API 응답 구조에 따라 처리
      let statements = [];
      if (response.success && response.data) {
        // 백엔드에서 {success: true, data: {...}} 형태로 온 경우
        statements = response.data.statements || [];
      } else if (response.statements) {
        // 백엔드에서 직접 {statements: [...], count: n} 형태로 온 경우
        statements = response.statements || [];
      }

      if (statements.length >= 0) { // 0건도 성공으로 처리
        const statementIds = statements.map(stmt => stmt.statement_id);
        
        setSelectedStatements(statementIds);
        
        // 성공 메시지에 임대사업자 정보 포함
        let successMessage = `납기일 ${daysBefore}일~${daysAfter}일 후 청구서 ${statements.length}건이 선택되었습니다.`;
        if (specificOwnerId) {
          const ownerName = propertyOwnersData?.data?.find(owner => owner.id === specificOwnerId)?.name || `ID: ${specificOwnerId}`;
          successMessage = `[${ownerName}] ${successMessage}`;
        } else if (historyFilters.property_owner_id) {
          const ownerName = propertyOwnersData?.data?.find(owner => owner.id === historyFilters.property_owner_id)?.name || `ID: ${historyFilters.property_owner_id}`;
          successMessage = `[${ownerName}] ${successMessage}`;
        } else {
          successMessage = `[전체 임대사업자] ${successMessage}`;
        }
        
        toast.success(successMessage);
      } else {
        console.error('❌ API 응답 실패:', response);
        const errorMessage = response.error || response.data?.error || '알 수 없는 오류';
        toast.error(`납기일 기준 청구서 조회에 실패했습니다: ${errorMessage}`);
      }
    } catch (error) {
      console.error('❌ 납기일 기준 선택 오류:', error);
      console.error('❌ 오류 상세:', error.response?.data);
      toast.error(`납기일 기준 청구서 선택에 실패했습니다: ${error.message}`);
    }
  };

  // 청구서 확정 함수
  const handleConfirmStatement = async (statementId) => {
    try {
      const response = await api.post(`/billing/statements/${statementId}/confirm`, {});
      if (response.data.success) {
        toast.success(response.data.message);
        // 빌링 내역 새로고침
        refetchHistory();
      } else {
        toast.error(response.data.error || '청구서 확정에 실패했습니다.');
      }
    } catch (error) {
      console.error('청구서 확정 오류:', error);
      toast.error('청구서 확정 중 오류가 발생했습니다.');
    }
  };

  // 일괄확정 함수
  const handleBatchConfirm = async () => {
    if (selectedStatements.length === 0) {
      toast.error('확정할 청구서를 선택해주세요.');
      return;
    }

    try {
      console.log('일괄확정 요청:', { statement_ids: selectedStatements });
      const response = await api.post('/billing/statements/batch-confirm', {
        statement_ids: selectedStatements
      });
      
      console.log('일괄확정 전체 응답:', response);
      console.log('일괄확정 응답 데이터:', response.data);
      console.log('응답 상태:', response.status);
      console.log('응답 헤더:', response.headers);
      console.log('success 값:', response.data?.success, '타입:', typeof response.data?.success);
      console.log('응답 전체 구조:', JSON.stringify(response, null, 2));
      
      // 응답 구조 확인: response.success 또는 response.data.success
      const isSuccess = (response.success === true) || (response.data && response.data.success === true);
      
      if (isSuccess) {
        const message = response.data?.message || response.message || '일괄확정이 완료되었습니다.';
        toast.success(message);
        setSelectedStatements([]);
        // 빌링 내역 새로고침
        try {
          await refetchHistory();
        } catch (refetchError) {
          console.error('빌링 내역 새로고침 오류:', refetchError);
        }
      } else {
        console.error('일괄확정 실패:', response.data);
        console.error('success 값이 false입니다:', response.data?.success);
        const errorMessage = response.data?.error || response.error || '일괄확정에 실패했습니다.';
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('일괄확정 오류:', error);
      toast.error('일괄확정 중 오류가 발생했습니다.');
    }
  };

  // 체크박스 선택 핸들러
  const handleSelectStatement = (statementId) => {
    setSelectedStatements(prev => 
      prev.includes(statementId) 
        ? prev.filter(id => id !== statementId)
        : [...prev, statementId]
    );
  };

  // 청구서 전체 선택/해제
  const handleSelectAllStatements = async () => {
    try {
      console.log('🔍 전체 선택 시작 - 현재 선택된 청구서 수:', selectedStatements.length);
      
      // 현재 선택된 임대사업자 필터를 포함한 파라미터 구성
      // 전달되지 않은 모든 항목(pending, confirmed 등)을 선택할 수 있도록 status 필터 제거
      const params = {
        page: 1,
        per_page: 1000 // 충분히 큰 수로 설정
      };
      
      // status 필터가 있으면 유지 (사용자가 필터를 적용한 경우)
      if (historyFilters.status && historyFilters.status !== 'delivered') {
        params.status = historyFilters.status;
      }
      
      // 현재 선택된 임대사업자가 있으면 필터에 추가
      if (historyFilters.property_owner_id) {
        params.property_owner_id = historyFilters.property_owner_id;
        console.log('🔍 임대사업자 필터 적용:', params.property_owner_id);
      }
      
      // 전체 대기 상태 청구서 조회 (페이징 없이)
      const response = await api.get('/billing-work/history', {
        params: params
      });
      
      console.log('📡 전체 응답 객체:', response);
      console.log('📡 API 응답 상태:', response.status);
      console.log('📡 API 응답 데이터:', response.data);
      console.log('📡 API 응답 헤더:', response.headers);
      
      // 응답 데이터가 있는지 확인
      if (response.data) {
        console.log('📡 응답 데이터 success 값:', response.data.success, '타입:', typeof response.data.success);
        console.log('📡 응답 데이터 구조:', JSON.stringify(response.data, null, 2));
      }
      
      // 응답 구조 확인: response.success 또는 response.data.success
      const isSuccess = (response.success === true) || (response.data && response.data.success === true);
      
      if (isSuccess) {
        // 응답 구조에 따라 데이터 경로 결정
        const historyData = response.data?.history || response.history || [];
        // 전달되지 않은 항목만 필터링 (delivered가 아닌 항목)
        const selectableIds = historyData
          .filter(item => item.status !== 'delivered')
          .map(item => item.statement_id);
        
        console.log('✅ 선택 가능한 청구서 수:', selectableIds.length);
        console.log('✅ 선택 가능한 청구서 ID들:', selectableIds);
        
        // 현재 선택된 항목과 선택 가능한 모든 항목을 비교
        const allSelected = selectableIds.length > 0 && 
          selectableIds.every(id => selectedStatements.includes(id));
        
        if (allSelected) {
          console.log('🔄 전체 해제 실행');
          setSelectedStatements([]);
          toast.success('전체 선택이 해제되었습니다.');
        } else {
          console.log('🔄 전체 선택 실행');
          setSelectedStatements(selectableIds);
          toast.success(`${selectableIds.length}건이 선택되었습니다.`);
        }
      } else {
        console.error('❌ API 응답 실패:', response.data);
        toast.error('전체 선택에 실패했습니다.');
      }
    } catch (error) {
      console.error('❌ 전체 선택 오류:', error);
      toast.error('전체 선택 중 오류가 발생했습니다.');
    }
  };

  // 빌링상세 보기 함수
  const handleViewStatementDetail = (statement) => {
    setSelectedStatement(statement);
    setShowStatementDetail(true);
  };

  // 빌링 실행 뮤테이션
  const executeBillingMutation = useMutation(
    (data) => api.post('/billing-work/start-billing', data),
    {
      onSuccess: (response) => {
        const result = response?.data || response || {};
        const processedContracts = result?.processed_contracts ?? 0;
        const message = response?.message || result?.message;

        if (processedContracts > 0) {
          toast.success(message || `빌링이 성공적으로 실행되었습니다. ${processedContracts}건의 청구서가 생성되었습니다.`);
        } else {
          toast.error(message || '빌링 작업은 완료되었지만 생성된 청구서가 없습니다.');
        }
        setShowCreateModal(false);
        setCreateForm({ billing_month: '', work_week_start: '', billing_type: 'weekly' });
        // 빌링 세션과 내역 모두 갱신
        refetchSessions();
        refetchHistory();
      },
      onError: (error) => {
        console.error('빌링 실행 오류:', error);
        toast.error(error.response?.data?.error || '빌링 실행에 실패했습니다.');
      }
    }
  );

  // 임대사업자 선택 핸들러
  const handleOwnerSelect = (ownerId) => {
    setSelectedOwnerIds(prev => {
      if (prev.includes(ownerId)) {
        return prev.filter(id => id !== ownerId);
      } else {
        return [...prev, ownerId];
      }
    });
  };

  // 전체 선택/해제 핸들러
  const handleSelectAll = () => {
    if (selectedOwnerIds.length === propertyOwnersData?.data?.length) {
      setSelectedOwnerIds([]);
    } else {
      setSelectedOwnerIds(propertyOwnersData?.data?.map(owner => owner.id) || []);
    }
  };

  const handleCreateBilling = () => {
    if (!createForm.billing_month) {
      toast.error('청구월을 입력해주세요.');
      return;
    }

    if (createForm.billing_type === 'weekly' && !createForm.work_week_start) {
      toast.error('작업 시작일을 입력해주세요.');
      return;
    }

    // 작업 주 계산 (주차별 빌링인 경우만)
    let workWeekStart = createForm.work_week_start;
    let workWeekEnd = createForm.work_week_start;

    if (createForm.billing_type === 'weekly') {
      const startDate = new Date(createForm.work_week_start);
      const endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 6);
      workWeekEnd = endDate.toISOString().split('T')[0];
    } else {
      // 전체 월 빌링인 경우 해당 월의 1일과 마지막일로 설정
      const monthStart = new Date(createForm.billing_month + '-01');
      const monthEnd = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0);
      workWeekStart = monthStart.toISOString().split('T')[0];
      workWeekEnd = monthEnd.toISOString().split('T')[0];
    }

    const billingData = {
      billing_month: createForm.billing_month,
      work_week_start: workWeekStart,
      work_week_end: workWeekEnd,
      selected_owner_ids: selectedOwnerIds, // 선택된 임대사업자 ID 목록 추가
      billing_type: createForm.billing_type // 빌링 유형 추가
    };

    executeBillingMutation.mutate(billingData);
  };

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">임대료 청구관리</h1>
        <p className="text-gray-600">임대료, 관리비, 주차비 및 추가 비용을 통합 청구합니다.</p>
        <div className="mt-4 p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-800">
            💡 소속 임대사업자를 선택하여 해당 임대사업자의 임차인들에게 일괄 청구할 수 있습니다.
          </p>
        </div>
      </div>

      {/* 소속 임대사업자 선택 */}
      {propertyOwnersData?.data && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">소속 임대사업자 선택</h3>
            <div className="flex items-center space-x-4">
              <button
                onClick={handleSelectAll}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                {selectedOwnerIds.length === propertyOwnersData.data.length ? '전체 해제' : '전체 선택'}
              </button>
              <span className="text-sm text-gray-500">
                {selectedOwnerIds.length} / {propertyOwnersData.data.length} 선택됨
              </span>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {propertyOwnersData.data.map((owner) => (
              <div 
                key={owner.id} 
                className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                  selectedOwnerIds.includes(owner.id) 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => handleOwnerSelect(owner.id)}
              >
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    checked={selectedOwnerIds.includes(owner.id)}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => handleOwnerSelect(owner.id)}
                    className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">{owner.name}</h4>
                    <p className="text-sm text-gray-600">{owner.company_name}</p>
                    <div className="mt-2 space-y-1">
                      <p className="text-sm text-gray-500">
                        🏢 건물: {owner.building_count}개
                      </p>
                      <p className="text-sm text-gray-500">
                        📋 활성 계약: {owner.active_contract_count}개
                      </p>
                      {owner.bank_name && (
                        <p className="text-sm text-gray-500">
                          🏦 {owner.bank_name} {owner.bank_account}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {selectedOwnerIds.length === 0 && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-sm text-blue-800">
                임대사업자를 선택하지 않으면 소속 전체 임대사업자를 대상으로 임대료 청구가 실행됩니다.
              </p>
            </div>
          )}
        </div>
      )}

      {/* 탭 네비게이션 */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('billing-work')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'billing-work'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              임대료 청구
            </button>
            <button
              onClick={() => setActiveTab('billing-history')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'billing-history'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              빌링 내역
            </button>
            <button
              onClick={() => setActiveTab('billing-reports')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'billing-reports'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              보고서
            </button>
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'billing-work' && (
            <div className="space-y-6">
              {/* 필터 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    청구월
                  </label>
                  <input
                    type="month"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    건물
                  </label>
                  <select
                    value={selectedBuilding}
                    onChange={(e) => setSelectedBuilding(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">전체</option>
                    {buildingsData?.data?.map((building) => (
                      <option key={building.id} value={building.id}>
                        {building.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* 빌링 실행 버튼 */}
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">통합 임대료 청구</h3>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  통합 빌링 실행
                </button>
              </div>

              {/* 빌링 세션 목록 */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">최근 빌링 세션</h4>
                {sessionsData?.data?.length > 0 ? (
                  <div className="space-y-2">
                    {sessionsData.data.slice(0, 5).map((session) => (
                      <div key={session.id} className="bg-white p-3 rounded border">
                        <div className="flex justify-between items-center">
                          <div>
                            <span className="font-medium">
                              {session.billing_month} {session.session_number_in_month}차
                            </span>
                            <span className="text-gray-500 ml-2">
                              {session.processed_contracts_count}건 처리
                            </span>
                            <span className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              session.status === 'completed' ? 'bg-green-100 text-green-800' :
                              session.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                              session.status === 'running' || session.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                              session.status === 'preparing' ? 'bg-gray-100 text-gray-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              {session.status === 'completed' ? '완료' :
                               session.status === 'cancelled' ? '취소' :
                               session.status === 'running' || session.status === 'in_progress' ? '진행중' :
                               session.status === 'preparing' ? '준비중' : '처리중'}
                            </span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-sm text-gray-500">
                              {new Date(session.created_at).toLocaleDateString()}
                            </span>
                            {session.status === 'completed' && (
                              <div className="flex space-x-1">
                                <button
                                  onClick={() => handleRecalculateSession(session.id)}
                                  className="px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded hover:bg-yellow-200"
                                  title="재계산"
                                >
                                  재계산
                                </button>
                              </div>
                            )}
                            {session.status === 'running' && (
                              <div className="flex space-x-1">
                                <button
                                  onClick={() => handleCancelSession(session.id)}
                                  className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded hover:bg-red-200"
                                  title="취소"
                                >
                                  취소
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500">빌링 세션이 없습니다.</p>
                )}
              </div>
            </div>
          )}

          {activeTab === 'history' && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">빌링 내역</h3>
              <p className="text-gray-500">임대사업자별 빌링 내역 조회 기능은 준비 중입니다.</p>
            </div>
          )}

          {activeTab === 'reports' && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">빌링 보고서</h3>
              <p className="text-gray-500">통합 빌링 보고서 기능은 준비 중입니다.</p>
            </div>
          )}
        </div>
      </div>

      {/* 빌링 실행 모달 */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">통합 빌링 실행</h3>
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <p className="text-sm text-yellow-800">
                  {selectedOwnerIds.length > 0 ? (
                    <>
                      ⚠️ 선택된 {selectedOwnerIds.length}명의 임대사업자의 임차인들에게 빌링이 실행됩니다.
                      <div className="mt-2">
                        <strong>선택된 임대사업자:</strong>
                        <ul className="mt-1 text-xs">
                          {propertyOwnersData?.data
                            ?.filter(owner => selectedOwnerIds.includes(owner.id))
                            .map(owner => (
                              <li key={owner.id}>• {owner.name} ({owner.company_name})</li>
                            ))
                          }
                        </ul>
                      </div>
                    </>
                  ) : (
                    '선택된 임대사업자가 없으므로 소속 전체 임대사업자의 임차인들에게 빌링이 실행됩니다.'
                  )}
                </p>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    청구월
                  </label>
                  <input
                    type="month"
                    value={createForm.billing_month}
                    onChange={(e) => setCreateForm({ ...createForm, billing_month: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    빌링 유형
                  </label>
                  <select
                    value={createForm.billing_type}
                    onChange={(e) => setCreateForm({ ...createForm, billing_type: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="weekly">주차별 빌링 (기존)</option>
                    <option value="monthly">전체 월 빌링 (신규)</option>
                  </select>
                </div>
                {createForm.billing_type === 'weekly' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      작업 시작일
                    </label>
                    <input
                      type="date"
                      value={createForm.work_week_start}
                      onChange={(e) => setCreateForm({ ...createForm, work_week_start: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  취소
                </button>
                <button
                  onClick={handleCreateBilling}
                  disabled={executeBillingMutation.isLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {executeBillingMutation.isLoading ? '실행 중...' : '통합 빌링 실행'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 빌링 내역 탭 */}
      {activeTab === 'billing-history' && (
        <div className="space-y-6">
          {/* 필터 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                청구월
              </label>
              <input
                type="month"
                value={historyFilters.billing_month}
                onChange={(e) => setHistoryFilters(prev => ({ ...prev, billing_month: e.target.value }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                임대사업자
              </label>
              <select
                value={historyFilters.property_owner_id}
                onChange={(e) => setHistoryFilters(prev => ({ ...prev, property_owner_id: e.target.value, building_id: '' }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">전체</option>
                {propertyOwnersData?.data?.map((owner) => (
                  <option key={owner.id} value={owner.id}>
                    {owner.name} ({owner.company_name})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                건물
              </label>
              <select
                value={historyFilters.building_id}
                onChange={(e) => setHistoryFilters(prev => ({ ...prev, building_id: e.target.value }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={!historyFilters.property_owner_id}
              >
                <option value="">전체</option>
                {buildingsData?.data?.map((building) => (
                  <option key={building.id} value={building.id}>
                    {building.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                납기일 시작 (일)
              </label>
              <input
                type="date"
                value={historyFilters.due_date_start}
                onChange={(e) => setHistoryFilters(prev => ({ ...prev, due_date_start: e.target.value }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="예: 2025-09-01 (1일부터)"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                납기일 종료 (일)
              </label>
              <input
                type="date"
                value={historyFilters.due_date_end}
                onChange={(e) => setHistoryFilters(prev => ({ ...prev, due_date_end: e.target.value }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="예: 2025-09-31 (31일까지)"
              />
            </div>
            <div className="flex items-end space-x-2">
              <button
                onClick={() => refetchHistory()}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                조회
              </button>
              <button
                onClick={() => setHistoryFilters({
                  billing_month: '',
                  property_owner_id: '',
                  building_id: '',
                  due_date_start: '',
                  due_date_end: ''
                })}
                className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                초기화
              </button>
            </div>
          </div>

          {/* 납기일 기준 자동 선택 */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
            <h4 className="text-sm font-medium text-yellow-800 mb-3">🎯 납기일 기준 자동 선택</h4>
            
            {/* 현재 필터 상태 표시 */}
            <div className="mb-3 p-2 bg-yellow-100 rounded text-xs text-yellow-800">
              <strong>현재 필터:</strong> 
              {historyFilters.property_owner_id ? (
                <span> 특정 임대사업자 (ID: {historyFilters.property_owner_id})</span>
              ) : (
                <span> 전체 임대사업자</span>
              )}
            </div>

            {/* 납기일 기준 선택 버튼들 */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
              <button
                onClick={() => handleSelectByDueDate(3, 5)}
                className="px-3 py-2 bg-yellow-600 text-white text-sm rounded-md hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              >
                3~5일 전 (기본)
              </button>
              <button
                onClick={() => handleSelectByDueDate(1, 3)}
                className="px-3 py-2 bg-orange-600 text-white text-sm rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                1~3일 전 (긴급)
              </button>
              <button
                onClick={() => handleSelectByDueDate(5, 7)}
                className="px-3 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                5~7일 전 (여유)
              </button>
              <button
                onClick={() => handleSelectByDueDate(7, 10)}
                className="px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                7~10일 전 (사전)
              </button>
            </div>

            {/* 임대사업자별 납기일 기준 선택 */}
            {propertyOwnersData?.data && propertyOwnersData.data.length > 0 && (
              <div className="mt-4">
                <h5 className="text-sm font-medium text-yellow-800 mb-2">🏢 임대사업자별 납기일 기준 선택</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {propertyOwnersData.data.map((owner) => (
                    <div key={owner.id} className="bg-white rounded border p-2">
                      <div className="text-xs font-medium text-gray-700 mb-1">{owner.name}</div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleSelectByDueDate(3, 5, owner.id)}
                          className="px-2 py-1 bg-yellow-500 text-white text-xs rounded hover:bg-yellow-600"
                          title={`${owner.name} - 3~5일 전`}
                        >
                          3~5일
                        </button>
                        <button
                          onClick={() => handleSelectByDueDate(1, 3, owner.id)}
                          className="px-2 py-1 bg-orange-500 text-white text-xs rounded hover:bg-orange-600"
                          title={`${owner.name} - 1~3일 전`}
                        >
                          1~3일
                        </button>
                        <button
                          onClick={() => handleSelectByDueDate(5, 7, owner.id)}
                          className="px-2 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600"
                          title={`${owner.name} - 5~7일 전`}
                        >
                          5~7일
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <p className="text-xs text-yellow-700 mt-2">
              * 위쪽 버튼은 현재 선택된 임대사업자 필터를 적용합니다. 아래쪽 버튼은 특정 임대사업자만 대상으로 합니다.
            </p>
          </div>

          {/* 빌링 내역 테이블 */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">빌링 내역</h3>
              {selectedStatements.length > 0 && (
                <div className="flex space-x-2">
                  <button
                    onClick={handleBatchConfirm}
                    className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    선택된 {selectedStatements.length}건 일괄확정
                  </button>
                  <button
                    onClick={handleOpenBatchDeliveryModal}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    선택된 {selectedStatements.length}건 일괄전달
                  </button>
                </div>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <input
                        type="checkbox"
                        checked={selectedStatements.length > 0}
                        onChange={(e) => {
                          e.preventDefault();
                          handleSelectAllStatements();
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      청구월
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      임대사업자
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      건물/세대
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      임차인
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      납기일
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      총 금액
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      상태
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      생성일
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      작업
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {historyData?.data?.history?.map((item) => (
                    <tr key={item.statement_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.status !== 'delivered' && (
                          <input
                            type="checkbox"
                            checked={selectedStatements.includes(item.statement_id)}
                            onChange={() => handleSelectStatement(item.statement_id)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.billing_month}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.property_owner_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.building_name} / {item.unit_number}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.tenant_name || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.due_date ? `${item.due_date}일` : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.total_amount?.toLocaleString()}원
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          item.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          item.status === 'confirmed' ? 'bg-blue-100 text-blue-800' :
                          item.status === 'delivered' ? 'bg-green-100 text-green-800' :
                          item.status === 'paid' ? 'bg-green-100 text-green-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {item.status === 'pending' ? '대기' :
                           item.status === 'confirmed' ? '확정' :
                           item.status === 'delivered' ? '전달완료' :
                           item.status === 'paid' ? '결제완료' : '연체'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(item.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleViewStatementDetail(item)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            상세보기
                          </button>
                          {item.status === 'pending' && (
                            <button
                              onClick={() => handleConfirmStatement(item.statement_id)}
                              className="text-orange-600 hover:text-orange-900"
                            >
                              확정
                            </button>
                          )}
                          {item.status !== 'delivered' && item.status !== 'pending' && (
                            <button
                              onClick={() => handleOpenDeliveryModal(item.statement_id)}
                              className="text-green-600 hover:text-green-900"
                            >
                              전달
                            </button>
                          )}
                          <button
                            onClick={() => handleSendRentDueKakao(item.statement_id)}
                            className="text-indigo-600 hover:text-indigo-900"
                          >
                            알림톡
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* 페이지네이션 */}
            {historyData?.data?.pagination && (
              <div className="px-6 py-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    총 {historyData.data.pagination.total_count}건 중 {((historyData.data.pagination.page - 1) * historyData.data.pagination.per_page) + 1}-{Math.min(historyData.data.pagination.page * historyData.data.pagination.per_page, historyData.data.pagination.total_count)}건 표시
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setHistoryPage(prev => Math.max(1, prev - 1))}
                      disabled={historyData.data.pagination.page <= 1}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      이전
                    </button>
                    <span className="px-3 py-1 text-sm text-gray-700">
                      {historyData.data.pagination.page} / {historyData.data.pagination.total_pages}
                    </span>
                    <button
                      onClick={() => setHistoryPage(prev => prev + 1)}
                      disabled={historyData.data.pagination.page >= historyData.data.pagination.total_pages}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      다음
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 빌링 보고서 탭 */}
      {activeTab === 'billing-reports' && (
        <div className="space-y-6">
          {/* 필터 */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                보고서 유형
              </label>
              <select
                value={reportType}
                onChange={(e) => setReportType(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="monthly">월별 집계</option>
                <option value="property_owner">임대사업자별 집계</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                시작월
              </label>
              <input
                type="month"
                value={reportFilters.start_month}
                onChange={(e) => setReportFilters(prev => ({ ...prev, start_month: e.target.value }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                종료월
              </label>
              <input
                type="month"
                value={reportFilters.end_month}
                onChange={(e) => setReportFilters(prev => ({ ...prev, end_month: e.target.value }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                임대사업자
              </label>
              <select
                value={reportFilters.property_owner_id}
                onChange={(e) => setReportFilters(prev => ({ ...prev, property_owner_id: e.target.value }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">전체</option>
                {propertyOwnersData?.data?.map((owner) => (
                  <option key={owner.id} value={owner.id}>
                    {owner.name} ({owner.company_name})
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => refetchReports()}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                조회
              </button>
            </div>
          </div>

          {/* 전체 통계 */}
          {reportsData?.data?.total_statistics && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="text-sm font-medium text-blue-600">총 청구서</div>
                <div className="text-2xl font-bold text-blue-900">
                  {reportsData.data.total_statistics.total_statements}건
                </div>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="text-sm font-medium text-green-600">총 금액</div>
                <div className="text-2xl font-bold text-green-900">
                  {reportsData.data.total_statistics.total_amount?.toLocaleString()}원
                </div>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg">
                <div className="text-sm font-medium text-purple-600">빌링 월 수</div>
                <div className="text-2xl font-bold text-purple-900">
                  {reportsData.data.total_statistics.total_months}개월
                </div>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg">
                <div className="text-sm font-medium text-orange-600">월평균 금액</div>
                <div className="text-2xl font-bold text-orange-900">
                  {reportsData.data.total_statistics.average_monthly_amount?.toLocaleString()}원
                </div>
              </div>
            </div>
          )}

          {/* 보고서 데이터 */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                {reportType === 'monthly' ? '월별 빌링 보고서' : '임대사업자별 빌링 보고서'}
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {reportType === 'monthly' ? (
                      <>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          청구월
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          청구서 수
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          총 금액
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          임대사업자 수
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          평균 금액
                        </th>
                      </>
                    ) : (
                      <>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          임대사업자
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          청구서 수
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          총 금액
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          빌링 월 수
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          평균 금액
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          월평균 금액
                        </th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {reportsData?.data?.report_data?.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      {reportType === 'monthly' ? (
                        <>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {item.billing_month}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {item.statement_count}건
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {item.total_amount?.toLocaleString()}원
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {item.property_owner_count}개
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {item.average_amount?.toLocaleString()}원
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {item.property_owner_name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {item.statement_count}건
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {item.total_amount?.toLocaleString()}원
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {item.billing_months}개월
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {item.average_amount?.toLocaleString()}원
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {item.monthly_average?.toLocaleString()}원
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* 빌링상세 모달 */}
      {showStatementDetail && selectedStatement && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  빌링상세 - {selectedStatement.billing_month}
                </h3>
                <button
                  onClick={() => setShowStatementDetail(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {statementDetailData?.data ? (
                <div className="space-y-6">
                  {/* 기본 정보 */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-900 mb-2">계약 정보</h4>
                      <div className="space-y-1 text-sm">
                        <p><span className="font-medium">계약번호:</span> {statementDetailData.data.contract_number}</p>
                        <p><span className="font-medium">계약기간:</span> {statementDetailData.data.contract_start_date} ~ {statementDetailData.data.contract_end_date}</p>
                        <p><span className="font-medium">납기일:</span> {statementDetailData.data.due_date ? `${statementDetailData.data.due_date}일` : '-'}</p>
                        <p><span className="font-medium">월세:</span> {statementDetailData.data.monthly_rent?.toLocaleString()}원</p>
                        <p><span className="font-medium">관리비:</span> {statementDetailData.data.maintenance_fee?.toLocaleString()}원</p>
                        <p><span className="font-medium">주차비:</span> {statementDetailData.data.parking_fee?.toLocaleString()}원</p>
                        <p><span className="font-medium">보증금:</span> {statementDetailData.data.deposit?.toLocaleString()}원</p>
                      </div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-900 mb-2">임차인 정보</h4>
                      <div className="space-y-1 text-sm">
                        <p><span className="font-medium">이름:</span> {statementDetailData.data.tenant?.name || '-'}</p>
                        <p><span className="font-medium">이메일:</span> {statementDetailData.data.tenant?.email || '-'}</p>
                        <p><span className="font-medium">전화번호:</span> {statementDetailData.data.tenant?.phone || '-'}</p>
                      </div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-900 mb-2">빌링 검증 정보</h4>
                      <div className="space-y-1 text-sm">
                        <p><span className="font-medium">청구월:</span> {statementDetailData.data.billing_month}</p>
                        <p><span className="font-medium">빌링세션:</span> {statementDetailData.data.work_session?.session_number || '-'}</p>
                        <p><span className="font-medium">생성일:</span> {new Date(statementDetailData.data.created_at).toLocaleDateString()}</p>
                        <p><span className="font-medium">상태:</span> 
                          <span className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            statementDetailData.data.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            statementDetailData.data.status === 'confirmed' ? 'bg-blue-100 text-blue-800' :
                            statementDetailData.data.status === 'delivered' ? 'bg-green-100 text-green-800' :
                            statementDetailData.data.status === 'paid' ? 'bg-green-100 text-green-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {statementDetailData.data.status === 'pending' ? '대기' :
                             statementDetailData.data.status === 'confirmed' ? '확정' :
                             statementDetailData.data.status === 'delivered' ? '전달완료' :
                             statementDetailData.data.status === 'paid' ? '결제완료' : '연체'}
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* 청구서 상세 항목 */}
                  {statementDetailData.data.line_items && statementDetailData.data.line_items.length > 0 && (
                    <div className="mt-6">
                      <h4 className="font-medium text-gray-900 mb-4">청구서 상세 항목</h4>
                      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">항목명</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">수량</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">단가</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">금액</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">비고</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {statementDetailData.data.line_items.map((item, index) => (
                              <tr key={index}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  {item.item_name}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {item.quantity}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {item.unit_price?.toLocaleString()}원
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  {item.total_amount?.toLocaleString()}원
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {item.description || '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* 건물 정보 */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-2">건물 정보</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <p><span className="font-medium">임대사업자:</span> {statementDetailData.data.property_owner?.name}</p>
                        <p><span className="font-medium">회사명:</span> {statementDetailData.data.property_owner?.company_name || '-'}</p>
                      </div>
                      <div>
                        <p><span className="font-medium">건물명:</span> {statementDetailData.data.building?.name}</p>
                        <p><span className="font-medium">주소:</span> {statementDetailData.data.building?.address}</p>
                      </div>
                      <div>
                        <p><span className="font-medium">세대번호:</span> {statementDetailData.data.unit?.unit_number}</p>
                        <p><span className="font-medium">면적:</span> {statementDetailData.data.unit?.area ? `${statementDetailData.data.unit.area}㎡` : '-'}</p>
                      </div>
                    </div>
                  </div>



                  {/* 미납액 정보 */}
                  {statementDetailData.data.overdue_info?.has_overdue && (
                    <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                      <h4 className="font-medium text-red-900 mb-3">⚠️ 미납액 정보</h4>
                      
                      {/* 미납액 요약 */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mb-4">
                        <div>
                          <p><span className="font-medium text-red-700">미납 건수:</span> {statementDetailData.data.overdue_info.overdue_count}건</p>
                          <p><span className="font-medium text-red-700">미납 총액:</span> {statementDetailData.data.overdue_info.overdue_amount?.toLocaleString()}원</p>
                        </div>
                        <div>
                          <p className="text-red-600 text-xs">
                            * 이전 청구서들의 미납액이 포함되어 있습니다.
                          </p>
                        </div>
                      </div>

                      {/* 월별 미납액 상세 */}
                      {statementDetailData.data.overdue_info.overdue_details && 
                       statementDetailData.data.overdue_info.overdue_details.length > 0 && (
                        <div className="bg-white border border-red-300 rounded-lg p-3">
                          <h5 className="font-medium text-red-800 mb-2">📅 월별 미납액 상세</h5>
                          <div className="space-y-2">
                            {statementDetailData.data.overdue_info.overdue_details.map((detail, index) => (
                              <div key={index} className="flex justify-between items-center py-1 border-b border-red-100 last:border-b-0">
                                <span className="text-sm text-red-700">
                                  {detail.billing_month}월분 월세
                                </span>
                                <span className="font-medium text-red-800">
                                  {detail.amount?.toLocaleString()}원
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* 빌링 검증 요약 */}
                  <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                    <h4 className="font-medium text-blue-900 mb-3">🔍 빌링 작업 검증 요약</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                      <div className="bg-white p-3 rounded border">
                        <p className="font-medium text-gray-700">청구 항목 수</p>
                        <p className="text-lg font-bold text-blue-600">{statementDetailData.data.line_items?.length || 0}개</p>
                      </div>
                      <div className="bg-white p-3 rounded border">
                        <p className="font-medium text-gray-700">부가세 검증</p>
                        <p className="text-lg font-bold text-green-600">
                          {statementDetailData.data.vat_info?.vat_calculation_correct ? '✅ 정확' : '❌ 오류'}
                        </p>
                        {statementDetailData.data.vat_info?.total_vat_amount > 0 && (
                          <p className="text-xs text-gray-500">
                            {statementDetailData.data.vat_info.total_vat_amount.toLocaleString()}원
                          </p>
                        )}
                      </div>
                      <div className="bg-white p-3 rounded border">
                        <p className="font-medium text-gray-700">미납액</p>
                        <p className="text-lg font-bold text-red-600">
                          {statementDetailData.data.overdue_info?.has_overdue ? 
                            `${statementDetailData.data.overdue_info.overdue_count}건` : '없음'}
                        </p>
                      </div>
                      <div className="bg-white p-3 rounded border">
                        <p className="font-medium text-gray-700">빌링 상태</p>
                        <p className="text-lg font-bold text-gray-600">
                          {statementDetailData.data.status === 'pending' ? '대기' :
                           statementDetailData.data.status === 'confirmed' ? '확정' :
                           statementDetailData.data.status === 'delivered' ? '전달완료' :
                           statementDetailData.data.status === 'paid' ? '결제완료' : '연체'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* 작업 버튼 */}
                  <div className="flex justify-between items-center">
                    <div className="text-sm text-gray-600">
                      최종 업데이트: {statementDetailData.data.updated_at ? 
                        new Date(statementDetailData.data.updated_at).toLocaleString() : 
                        new Date(statementDetailData.data.created_at).toLocaleString()}
                    </div>
                    <div className="flex space-x-2">
                      {statementDetailData.data.status !== 'delivered' && (
                        <button
                          onClick={() => handleOpenDeliveryModal(statementDetailData.data.statement_id)}
                          className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                        >
                          청구서 전달
                        </button>
                      )}
                      <button
                        onClick={() => setShowStatementDetail(false)}
                        className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
                      >
                        닫기
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-2 text-sm text-gray-600">빌링상세를 불러오는 중...</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 청구서 전달 모달 */}
      {showDeliveryModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">청구서 전달</h3>
              
              <div className="space-y-4">
                {/* 전달 방법 선택 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    전달 방법
                  </label>
                  <div className="flex space-x-4">
                    <label className={`flex items-center ${isMobile() ? 'opacity-50 cursor-not-allowed' : ''}`}>
                      <input
                        type="radio"
                        value="email"
                        checked={deliveryForm.method === 'email'}
                        onChange={(e) => setDeliveryForm({ ...deliveryForm, method: e.target.value })}
                        className="mr-2"
                        disabled={isMobile()}
                      />
                      <span className={isMobile() ? 'text-gray-400' : ''}>
                        이메일 {isMobile() && '(PC에서만)'}
                      </span>
                    </label>
                    <label className={`flex items-center ${!isMobile() ? 'opacity-50 cursor-not-allowed' : ''}`}>
                      <input
                        type="radio"
                        value="sms"
                        checked={deliveryForm.method === 'sms'}
                        onChange={(e) => setDeliveryForm({ ...deliveryForm, method: e.target.value })}
                        className="mr-2"
                        disabled={!isMobile()}
                      />
                      <span className={!isMobile() ? 'text-gray-400' : ''}>
                        SMS {!isMobile() && '(모바일에서만)'}
                      </span>
                    </label>
                  </div>
                  {isMobile() && (
                    <p className="text-xs text-blue-600 mt-1">
                      📱 모바일에서는 SMS 앱을 통해 발송됩니다.
                    </p>
                  )}
                  {!isMobile() && (
                    <p className="text-xs text-blue-600 mt-1">
                      💻 PC에서는 이메일을 통해 발송됩니다.
                    </p>
                  )}
                </div>

                {/* 템플릿 선택 */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      메시지 템플릿
                    </label>
                    {deliveryForm.template_id && (
                      <button
                        type="button"
                        onClick={() => setDeliveryForm({ ...deliveryForm, template_id: '', custom_message: '' })}
                        className="text-xs text-red-600 hover:text-red-800"
                      >
                        템플릿 선택 해제
                      </button>
                    )}
                  </div>
                  <div className="space-y-2">
                    {messageTemplatesData?.data?.filter(template => 
                      template.template_type === deliveryForm.method && 
                      template.category === 'billing'
                    ).map((template) => (
                      <div 
                        key={template.id} 
                        className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                          deliveryForm.template_id === template.id 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => {
                          const processedContent = statementDetailData?.data ? 
                            replaceTemplateVariables(template.content, statementDetailData.data) : 
                            template.content;
                          setDeliveryForm({ 
                            ...deliveryForm, 
                            template_id: template.id,
                            custom_message: processedContent // 변수 치환된 템플릿 내용을 메시지에 복사
                          });
                        }}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900 mb-1">
                              {template.template_name}
                            </h4>
                            <p className="text-sm text-gray-600 mb-2">
                              {template.subject}
                            </p>
                            <div className="text-sm text-gray-700 bg-gray-50 p-2 rounded border max-h-20 overflow-y-auto">
                              {statementDetailData?.data ? 
                                replaceTemplateVariables(template.content, statementDetailData.data) : 
                                template.content
                              }
                            </div>
                          </div>
                          <div className="ml-3">
                            <input
                              type="radio"
                              checked={deliveryForm.template_id === template.id}
                              onChange={() => {
                                const processedContent = statementDetailData?.data ? 
                                  replaceTemplateVariables(template.content, statementDetailData.data) : 
                                  template.content;
                                setDeliveryForm({ 
                                  ...deliveryForm, 
                                  template_id: template.id,
                                  custom_message: processedContent
                                });
                              }}
                              className="text-blue-600 focus:ring-blue-500"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                    {messageTemplatesData?.data?.filter(template => 
                      template.template_type === deliveryForm.method && 
                      template.category === 'billing'
                    ).length === 0 && !messageTemplatesData?.isLoading && (
                      <p className="text-sm text-gray-500 text-center py-4">
                        사용 가능한 템플릿이 없습니다. 직접 메시지를 입력하거나 템플릿을 생성해주세요.
                      </p>
                    )}
                  </div>
                </div>

                {/* 사용자 정의 메시지 */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      메시지 내용
                    </label>
                    {deliveryForm.template_id && (
                      <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">
                        템플릿에서 복사됨
                      </span>
                    )}
                  </div>
                  <textarea
                    value={deliveryForm.custom_message}
                    onChange={(e) => setDeliveryForm({ ...deliveryForm, custom_message: e.target.value })}
                    placeholder="메시지 내용을 입력하거나 템플릿을 선택하세요..."
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={6}
                  />
                  <div className="mt-2 flex justify-between items-center text-xs text-gray-500">
                    <span>
                      {deliveryForm.custom_message.length}자
                    </span>
                    <div className="flex space-x-2">
                      <button
                        type="button"
                        onClick={() => setDeliveryForm({ ...deliveryForm, custom_message: '' })}
                        className="text-red-600 hover:text-red-800"
                      >
                        내용 지우기
                      </button>
                      {deliveryForm.custom_message && (
                        <button
                          type="button"
                          onClick={() => {
                            const processedContent = statementDetailData?.data ? 
                              replaceTemplateVariables(deliveryForm.custom_message, statementDetailData.data) : 
                              deliveryForm.custom_message;
                            setDeliveryForm({ ...deliveryForm, custom_message: processedContent });
                          }}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          변수 치환
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* 메시지 미리보기 */}
                  {deliveryForm.custom_message && (
                    <div className="mt-3 p-3 bg-gray-50 border rounded-lg">
                      <h5 className="text-sm font-medium text-gray-700 mb-2">미리보기:</h5>
                      <div className="text-sm text-gray-600 whitespace-pre-wrap">
                        {statementDetailData?.data ? 
                          replaceTemplateVariables(deliveryForm.custom_message, statementDetailData.data) : 
                          deliveryForm.custom_message
                        }
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowDeliveryModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  취소
                </button>
                <button
                  onClick={handleDeliverStatement}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  전달
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 일괄발송 모달 */}
      {showBatchDeliveryModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                일괄 전달 ({selectedStatements.length}건)
              </h3>
              
              <div className="space-y-4">
                {/* 전달 방법 선택 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    전달 방법
                  </label>
                  <div className="flex space-x-4">
                    <label className={`flex items-center ${isMobile() ? 'opacity-50 cursor-not-allowed' : ''}`}>
                      <input
                        type="radio"
                        value="email"
                        checked={batchDeliveryForm.method === 'email'}
                        onChange={(e) => setBatchDeliveryForm({ ...batchDeliveryForm, method: e.target.value })}
                        className="mr-2"
                        disabled={isMobile()}
                      />
                      <span className={isMobile() ? 'text-gray-400' : ''}>
                        이메일 {isMobile() && '(PC에서만)'}
                      </span>
                    </label>
                    <label className={`flex items-center ${!isMobile() ? 'opacity-50 cursor-not-allowed' : ''}`}>
                      <input
                        type="radio"
                        value="sms"
                        checked={batchDeliveryForm.method === 'sms'}
                        onChange={(e) => setBatchDeliveryForm({ ...batchDeliveryForm, method: e.target.value })}
                        className="mr-2"
                        disabled={!isMobile()}
                      />
                      <span className={!isMobile() ? 'text-gray-400' : ''}>
                        SMS {!isMobile() && '(모바일에서만)'}
                      </span>
                    </label>
                  </div>
                  {isMobile() && (
                    <p className="text-xs text-blue-600 mt-1">
                      📱 모바일에서는 SMS 앱을 통해 순차적으로 발송됩니다.
                    </p>
                  )}
                  {!isMobile() && (
                    <p className="text-xs text-blue-600 mt-1">
                      💻 PC에서는 이메일을 통해 일괄 발송됩니다.
                    </p>
                  )}
                </div>

                {/* 템플릿 선택 */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      메시지 템플릿
                    </label>
                    {batchDeliveryForm.template_id && (
                      <button
                        type="button"
                        onClick={() => setBatchDeliveryForm({ ...batchDeliveryForm, template_id: '', custom_message: '' })}
                        className="text-xs text-red-600 hover:text-red-800"
                      >
                        템플릿 선택 해제
                      </button>
                    )}
                  </div>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {messageTemplatesData?.data?.filter(template => 
                      template.template_type === batchDeliveryForm.method && 
                      template.category === 'billing'
                    ).map((template) => (
                      <div 
                        key={template.id} 
                        className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                          batchDeliveryForm.template_id === template.id 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => {
                          setBatchDeliveryForm({ 
                            ...batchDeliveryForm, 
                            template_id: template.id,
                            custom_message: template.content
                          });
                        }}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900 mb-1">
                              {template.template_name}
                            </h4>
                            <p className="text-sm text-gray-600 mb-2">
                              {template.subject}
                            </p>
                            <div className="text-sm text-gray-700 bg-gray-50 p-2 rounded border max-h-20 overflow-y-auto">
                              {template.content}
                            </div>
                          </div>
                          <div className="ml-3">
                            <input
                              type="radio"
                              checked={batchDeliveryForm.template_id === template.id}
                              onChange={() => {
                                setBatchDeliveryForm({ 
                                  ...batchDeliveryForm, 
                                  template_id: template.id,
                                  custom_message: template.content
                                });
                              }}
                              className="text-blue-600 focus:ring-blue-500"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 사용자 정의 메시지 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    사용자 정의 메시지
                  </label>
                  <textarea
                    value={batchDeliveryForm.custom_message}
                    onChange={(e) => setBatchDeliveryForm({ ...batchDeliveryForm, custom_message: e.target.value })}
                    placeholder="메시지를 입력하세요..."
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <div className="mt-2 flex justify-between items-center text-xs text-gray-500">
                    <span>
                      {batchDeliveryForm.custom_message.length}자
                    </span>
                    <div className="flex space-x-2">
                      <button
                        type="button"
                        onClick={() => setBatchDeliveryForm({ ...batchDeliveryForm, custom_message: '' })}
                        className="text-red-600 hover:text-red-800"
                      >
                        내용 지우기
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowBatchDeliveryModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  취소
                </button>
                <button
                  onClick={handleBatchDeliver}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  일괄 전달
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RentalManagementCompanyBilling;
